import { gql } from '@apollo/client'
import { AiIcon, BodyText, Button, Card, Chip, Heading } from '@farol-ds/react'
import { ButtonKindEnum, ChipKindEnum } from '@jusbrasil-web/shared-apollo-client/types'
import classNames from 'classnames'
import { HTMLAttributes } from 'react'

import { useChooseFrequencyLaterEvents } from '../../../../hooks/use-choose-frequency-later-events'
import { BenefitsList } from '../../../benefits-list'
import { LockIcon } from '../../../lock-icon'
import { StepButtonLabels } from '../../types'
import { resolveOfferLockButton } from '../../utils'
import { ChooseFrequencyLaterOfferCard_OfferLockCardFragment } from './__generated__/choose-frequency-later-offer-card.graphql'
import styles from './choose-frequency-later-offer-card.module.scss'
import { ChooseFrequencyLaterOfferCardPrice } from './choose-frequency-later-offer-card-price'

export interface ChooseFrequencyLaterOfferCardProps extends HTMLAttributes<HTMLDivElement> {
  offerLockCard: ChooseFrequencyLaterOfferCard_OfferLockCardFragment
  className?: string
  onBtnClick: (title: string) => void
}

export function ChooseFrequencyLaterOfferCard({
  offerLockCard,
  className,
  onBtnClick,
}: ChooseFrequencyLaterOfferCardProps) {
  const { title, subtitle, offer, buttons, icon, infoChip } = offerLockCard
  const { isRecommendedOffer } = offer
  const { label: buttonLabel, kind: buttonKind } = resolveOfferLockButton(buttons, 0, {
    label: StepButtonLabels.SELECT_OFFER,
    kind: ButtonKindEnum.Primary,
  })

  const { metricsRef, sendPlanSelectedEvent } = useChooseFrequencyLaterEvents()

  function handleSelectOffer() {
    sendPlanSelectedEvent(title, offerLockCard)
    onBtnClick(offerLockCard.title)
  }

  return (
    <div
      className={classNames(styles.container, className, {
        [styles.recommendedContainer]: isRecommendedOffer,
      })}
    >
      <BodyText size="md" className={styles.recommendedText}>
        {isRecommendedOffer && 'Recomendado'}
      </BodyText>

      <Card className={styles.content}>
        <div className={classNames(styles.header, { [styles.headerWithInfoChip]: !!infoChip })}>
          <div className={styles.headerTitleContainer}>
            <div className={styles.headerTitle}>
              <LockIcon icon={icon} />
              <Heading size="md">{title}</Heading>
            </div>
          </div>

          <BodyText size="md">{subtitle}</BodyText>
        </div>

        {infoChip?.label && (
          <Chip
            size="md"
            kind={infoChip.kind ?? ChipKindEnum.Info}
            leftIcon={<AiIcon />}
            className={styles.infoChip}
          >
            {infoChip.label}
          </Chip>
        )}

        <ChooseFrequencyLaterOfferCardPrice offerLockOffer={offer} />

        <div className={styles.paymentButtons}>
          <Button kind={buttonKind} onClick={handleSelectOffer} ref={metricsRef}>
            {buttonLabel}
          </Button>
        </div>

        <div>
          <BenefitsList offerLockCard={offerLockCard} />
        </div>
      </Card>
    </div>
  )
}

ChooseFrequencyLaterOfferCard.fragments = {
  offerLockCard: gql`
    fragment ChooseFrequencyLaterOfferCard_offerLockCard on OfferLockCard {
      title
      subtitle
      icon {
        ...LockIcon_offerLockIcon
      }
      infoChip {
        label
        kind
      }
      offer {
        isRecommendedOffer
        priceAfterPromotionalPeriodText
        ...ChooseFrequencyLaterOfferCardPrice_offerLockOffer
      }
      buttons {
        label
        kind
      }
      ...BenefitsList_offerLockCard
      ...UseChooseFrequencyLaterEvents_offerLockCard
    }
    ${LockIcon.fragments.icon}
    ${ChooseFrequencyLaterOfferCardPrice.fragments.offerLockOffer}
    ${BenefitsList.fragments.offerLockCard}
    ${useChooseFrequencyLaterEvents.fragments.offerLockCard}
  `,
}
